//@ts-check
import React, { useState, useRef, useCallback } from "react";
import Modal from "react-bootstrap/lib/Modal";
import ClickTracker from "@components/click-tracker";
import Asset from "./teaser/Asset";
import { CommentButton, SocialButtons } from "./teaser/SocialButtons";
import { CommentContainer, useCommentCount, getArgs } from "@components/comments";
import useTeaserElement from "./util/hooks/useTeaserElement";
import { splitId } from "./admin/manager";
import useDetail from "./util/hooks/useDetail";
import useAnchor from "@util/hooks/useAnchor";

const Content = ({ detailId, detailAsset, itemId, item, anchor }) => {
  const teaserId = splitId(item.teaser_id);

  /**
   * @typedef {[
   *  string,
   *  string,
   *  import('mibaby-frontend/types').SupportedMimeTypes,
   *  import('mibaby-frontend/types').AssetVersion
   * ]} Content
   *
   * @type {Content}
   */
  const [status, html, detail_asset_mimetype, detail_asset_versions] = useDetail(
    detailId,
    detailAsset
  );

  const [teaserStatus, teaserElement] = useTeaserElement(item && item.detail_teaser, itemId);

  // Scroll to anchor
  /** @type {import("react").MutableRefObject<HTMLDivElement>} */
  const content = useRef();
  const [, , forceAnchor, scrollToAnchor] = useAnchor(content, anchor, [status]);

  // Comments
  const [showComments, setShowComments] = useState(true);
  const toggleComments = () => setShowComments(v => (v || forceAnchor("comments"), !v));
  const scrollToComments = useCallback(() => {
    setShowComments(true);
    forceAnchor("comments");
  }, []);
  const commentCount = useCommentCount(getArgs("ContentTeaser", teaserId));

  const filterClickForTracker = ev => {
    const tagName = ev.target.tagName.toLowerCase();
    return tagName === "a" || tagName === "button";
  };

  const hasSocialButtons = item && item.social_buttons && item.social_buttons.length;

  switch (status) {
    case "loading":
      return (
        <div className="modal-body text-center">
          <img src="/img/loading.gif" />
        </div>
      );
    case "loaded":
      return (
        <div ref={content}>
          {!hasSocialButtons && commentCount > 0 && (
            <div className="modal-body text-center">
              <a onClick={scrollToComments}>{commentCount} Kommentare</a>
            </div>
          )}
          {(teaserStatus === "loading" && <div className="text-center">{teaserElement}</div>) ||
            teaserElement}
          <ClickTracker name="feed_checkpoint" aid={itemId} filter={filterClickForTracker}>
            <Asset
              teaserId={teaserId}
              asset_mimetype={detail_asset_mimetype}
              asset_versions={detail_asset_versions}
              imageProps={{ sizes: "(min-width: 36em) 600px, 100vw" }}
            />
          </ClickTracker>
          {hasSocialButtons && (
            <SocialButtons
              id={itemId}
              teaserId={teaserId}
              buttons={item.social_buttons}
              AGG_LIKE={item.AGG_LIKE}
              showCommentBox={false}
              commentProps={{ onLoad: scrollToComments }}
            />
          )}
          <ClickTracker name="feed_checkpoint" aid={itemId} filter={filterClickForTracker}>
            <div
              dangerouslySetInnerHTML={{
                __html: html,
              }}
            />
          </ClickTracker>
          {item && (
            // TODO: This should be handled by the SocialButtons component
            <>
              <a name="comments" />
              <div className="list-group comments" style={{ marginBottom: 0 }}>
                <div className="list-group-item">
                  <CommentButton
                    modelType="ContentTeaser"
                    modelId={teaserId}
                    onClick={toggleComments}
                  />
                </div>
              </div>
              {showComments && (
                <CommentContainer
                  modelType="ContentTeaser"
                  modelId={teaserId}
                  autoload={true}
                  onLoaded={scrollToAnchor}
                />
              )}
            </>
          )}
        </div>
      );
    default:
      return <div className="modal-body">{status}</div>;
  }
};

export default ({ onHide, ...props }) => (
  <Modal
    container={document.getElementById("content")}
    show={true}
    onHide={onHide}
    className="modal-mobile-fullscreen"
  >
    <div className="modal-body-overflow-x">
      {!props.detailAsset ? (
        <Modal.Body className="clearfix">
          <button className="close" onClick={onHide}>
            &times;
          </button>
        </Modal.Body>
      ) : (
        <div className="modal-body-floating-close">
          <button className="close" onClick={onHide}>
            &times;
          </button>
        </div>
      )}

      <Content {...props} />
    </div>
  </Modal>
);
